import { useQuery } from "react-query"
import UserService from "~services/UserService"
import { useAuthContext } from "~context/authContext"
import { ASSIGNEE_TYPE } from "~constants/proofOfOwnership"
import { PooAssignee } from "~shapes/proofOfOwnership"

// Turns a POO assignee into a human-readable label:
//   - null when there's no assignee OR it's the current operator (no point
//     telling you "you" — keeps own rows / My validations clean)
//   - the SYSTEM name as-is (it's already readable, e.g. "auto-validator")
//   - the operator's email for another USER assignee
// USER assignee ids are user hashids (the same id space the page already resolves
// for the POO owner), so we look the operator up by id. Falls back to `@hashid`
// while the lookup is in flight or if it fails, so a foreign assignee is never
// shown empty.
const useOperatorLabel = (assignee?: PooAssignee | null): string | null => {
  const { loggedUser } = useAuthContext()

  const isUser = assignee?.type === ASSIGNEE_TYPE.USER
  const isMe = isUser && !!loggedUser?.id && assignee?.id === loggedUser.id
  const shouldFetch = !!assignee && isUser && !isMe

  const { data: operator } = useQuery(
    ["operator-user", assignee?.id],
    () => UserService.getUserById(assignee!.id, false),
    { enabled: shouldFetch, staleTime: 5 * 60 * 1000 },
  )

  if (!assignee) return null
  if (assignee.type === ASSIGNEE_TYPE.SYSTEM) return assignee.id
  if (isMe) return null
  return operator?.email || `@${assignee.id}`
}

export default useOperatorLabel
